import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import CloseGlyph from '../icons/CloseGlyph';
import useStyles from './VisualizationZoomDialog.styles';
import ZoomableImage from './ZoomableImage';

/**
 * Id linking the header title to the `Dialog` via `aria-labelledby`, so
 * screen readers announce the chart name (or fallback) instead of an
 * unlabeled dialog.
 */
const DIALOG_TITLE_ID = 'ai-sidekick-visualization-zoom-title';

export type VisualizationZoomDialogProps = {
  /** Whether the zoom dialog is visible. */
  open: boolean;
  /** HTTPS source of the visualization image being previewed. */
  src: string;
  /** Accessible alt text for the previewed image. */
  alt: string;
  /**
   * Optional per-image title shown in the header. Falls back to a
   * generic label when the BE does not supply one (SDPCDXP-1499).
   */
  title?: string;
  /** Fired when the user dismisses the dialog (close, Esc, backdrop). */
  onClose: () => void;
};

/**
 * Full-viewport lightbox for a single Sidekick visualization image.
 *
 * Renders above the chat panel via the MUI `Dialog` portal with a
 * scrim backdrop, a header (title + close), and a keyed
 * {@link ZoomableImage} that owns zoom/pan. Closing unmounts the
 * body and a new `src` remounts it, so each preview starts
 * fit-to-view without any reset effect.
 */
function VisualizationZoomDialog({
  open,
  src,
  alt,
  title,
  onClose,
}: VisualizationZoomDialogProps) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  const resolvedTitle =
    title !== undefined && title.trim() !== ''
      ? title
      : t('chat.visualization.dialog.titleFallback');

  return (
    <Dialog
      aria-labelledby={DIALOG_TITLE_ID}
      classes={{ root: classes.root, paper: classes.paper }}
      data-testid="ai-sidekick-visualization-zoom-dialog"
      fullWidth
      maxWidth={false}
      onClose={onClose}
      open={open}
      slotProps={{ backdrop: { className: classes.backdrop } }}
    >
      <Box className={classes.header}>
        <Typography
          className={classes.title}
          component="h2"
          id={DIALOG_TITLE_ID}
          title={resolvedTitle}
        >
          {resolvedTitle}
        </Typography>
        <IconButton
          aria-label={t('chat.visualization.dialog.closeAriaLabel')}
          className={classes.iconButton}
          data-testid="ai-sidekick-visualization-zoom-close"
          onClick={onClose}
        >
          <CloseGlyph />
        </IconButton>
      </Box>
      <ZoomableImage key={src} alt={alt} src={src} />
    </Dialog>
  );
}

export default VisualizationZoomDialog;
